import { type RefObject, useLayoutEffect, useState } from "react";

/**
 * The current layout width of an element in css px, kept up to date through
 * a ResizeObserver. `null` until the first layout (and where ResizeObserver
 * is not available), so callers can fall back to CSS-only sizing.
 *
 * Layout width, not the bounding box: a dialog that animates in with a scale
 * transform would otherwise report a smaller width on first paint, and a
 * ResizeObserver never fires for a transform, so the value would stay wrong.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    setWidth(element.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setWidth(entry ? entry.contentRect.width : element.clientWidth);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}
